import { baseApi } from "../../../app/api";
import { store } from "../../../app/store"; // Import store to get current language
import { formatMinorUnitString } from "../../../common/helpers/formatPrice";
// Import Raw API response types from the shared location
import type {
    RawCart,
    RawCartItem,
    RawCartTotals,
    RawCartItemTotals,
    RawCartFee,
    RawCartShippingRate,
    RawCartCoupon,
    RawCartItemAttribute,
    RawCartItemAttributeTerm,
    RawCartShippingPackage,
    RawCartShippingAddress,
    RawCartBillingAddress,
} from "../../../types/api/wcStoreApi/wcStoreApiCartEndpointTypes";

// Import Transformed types used by the application
import type {
    TransformedCart,
    TransformedCartItem,
    TransformedCartTotals,
    TransformedCartItemTotals,
    TransformedCartFee,
    TransformedCartShippingRate,
    TransformedCartCoupon,
    TransformedCartItemVariation,
    TransformedCartShippingPackage,
    TransformedCartItemAttribute,
    TransformedCartItemAttributeTerm,
    TransformedAddress, // Used as input/output
    AddCartItemParams,
    UpdateCartItemParams,
    RemoveCartItemParams,
    ApplyCouponParams,
    RemoveCouponParams,
    UpdateCustomerParams,
    SelectShippingRateParams,
} from "../types/cartApiTypes";

// Import product transformation logic for cross-sells
import { transformProductData } from "../../product/api/productApi";
import type { TransformedProduct } from "../../product/types/productApiTypes";

// --- API Slice Definition ---

const WC_STORE_API_BASE =
    window.themeVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

// --- Helper Function: Transform Application Address (camelCase) to API Raw Address (snake_case) ---
// Used ONLY for preparing the request body for updateCustomer mutation
export const transformAddressToRaw = (
    address: Partial<TransformedAddress> | undefined
): Partial<RawCartBillingAddress | RawCartShippingAddress> | undefined => {
    if (!address || Object.keys(address).length === 0) return undefined;

    const rawAddress: Partial<RawCartBillingAddress | RawCartShippingAddress> =
        {};

    // Map camelCase from TransformedAddress to snake_case for RawCart...Address
    if (address.firstName !== undefined)
        rawAddress.first_name = address.firstName;
    if (address.lastName !== undefined) rawAddress.last_name = address.lastName;
    if (address.company !== undefined) rawAddress.company = address.company;
    if (address.address1 !== undefined) rawAddress.address_1 = address.address1;
    if (address.address2 !== undefined) rawAddress.address_2 = address.address2;
    if (address.city !== undefined) rawAddress.city = address.city; // District name
    if (address.state !== undefined) rawAddress.state = address.state; // State code (e.g., TR16)

    // START MODIFICATION: Robust Postcode Handling
    // First, trim any whitespace. Then, if the result is an empty string,
    // provide a valid placeholder to satisfy strict validation.
    const trimmedPostcode = address.postcode?.trim() || "";
    rawAddress.postcode = trimmedPostcode === "" ? "11111" : trimmedPostcode;
    // END MODIFICATION

    if (address.country !== undefined) rawAddress.country = address.country; // Country code (e.g., TR)
    if (address.phone !== undefined) rawAddress.phone = address.phone;
    // Email is specific to billing
    if ("email" in address && address.email !== undefined) {
        (rawAddress as Partial<RawCartBillingAddress>).email = address.email;
    }

    return Object.keys(rawAddress).length > 0 ? rawAddress : undefined;
};

// --- Helper Function: Transform API Raw Address (snake_case) to Application Address (camelCase) ---
// Used in transformCartData for processing responses
export const transformAddress = (
    rawAddress: RawCartShippingAddress | RawCartBillingAddress | null | undefined
): TransformedAddress => {
    const defaults: TransformedAddress = {
        firstName: "",
        lastName: "",
        company: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postcode: "",
        country: "",
        email: "",
        phone: "",
    };
    if (!rawAddress) return defaults;
    const transformed: TransformedAddress = {
        firstName: rawAddress.first_name || defaults.firstName,
        lastName: rawAddress.last_name || defaults.lastName,
        company: rawAddress.company || defaults.company,
        address1: rawAddress.address_1 || defaults.address1,
        address2: rawAddress.address_2 || defaults.address2,
        city: rawAddress.city || defaults.city, // District Name
        state: rawAddress.state || defaults.state, // State Code (e.g., TR16)
        postcode: rawAddress.postcode || defaults.postcode,
        country: rawAddress.country || defaults.country, // Country Code (e.g., TR)
        phone: rawAddress.phone || defaults.phone,
    };
    if ("email" in rawAddress && rawAddress.email) {
        transformed.email = rawAddress.email;
    }
    return transformed;
};

// --- Helper Function: Transform Cart Item Attribute Term (Raw to Transformed) ---
export const transformCartItemAttributeTerm = (
    rawTerm: RawCartItemAttributeTerm
): TransformedCartItemAttributeTerm => {
    return {
        id: rawTerm.id,
        name: rawTerm.translated_name ?? rawTerm.name, // Use translated name if available
        slug: rawTerm.slug,
        type: rawTerm.type, // Pass through type (color, button-text, button-image)
        color: rawTerm.color,
        buttonText: rawTerm["button-text"], // Access using bracket notation
        buttonImage: rawTerm["button-image"], // Access using bracket notation
    };
};

// --- Helper Function: Transform Cart Item Attribute (Raw to Transformed) ---
export const transformCartItemAttribute = (
    rawAttribute: RawCartItemAttribute
): TransformedCartItemAttribute | null => {
    // Ensure terms exist and contain exactly one term (as expected for cart item attributes)
    if (!rawAttribute.terms || rawAttribute.terms.length !== 1) {
        console.warn(
            "Unexpected term structure in cart item attribute:",
            rawAttribute
        );
        return null;
    }
    const transformedTerm = transformCartItemAttributeTerm(rawAttribute.terms[0]);
    return {
        id: rawAttribute.id,
        name: rawAttribute.translated_name ?? rawAttribute.name, // Use translated attribute name
        taxonomy: rawAttribute.taxonomy,
        hasVariations: rawAttribute.has_variations,
        displayType: rawAttribute.display_type,
        term: transformedTerm, // Include the single transformed term
    };
};

// --- Main Transformation Function (Raw Cart -> Transformed Cart) ---
export const transformCartData = (rawCart: RawCart): TransformedCart => {
    const currencyMinorUnit =
        rawCart.totals?.currency_minor_unit ??
        window.themeVariables?.currencyMinorUnit ??
        2;
    const state = store.getState();
    const currentLanguage = state.language.currentLanguage;

    const transformCartItem = (rawItem: RawCartItem): TransformedCartItem => {
        const divisor = Math.pow(10, currencyMinorUnit);
        const price = parseFloat(rawItem.prices.price) / divisor;
        const regularPrice = parseFloat(rawItem.prices.regular_price) / divisor;
        const salePrice = parseFloat(rawItem.prices.sale_price) / divisor;
        const translations =
            rawItem.extensions?.product_translations?.[currentLanguage];
        const finalName = translations?.name ?? rawItem.name;
        const finalShortDescription =
            translations?.short_description ?? rawItem.short_description;
        const finalDescription = translations?.description ?? rawItem.description;

        const rawAttributes =
            rawItem.extensions?.enriched_attributes_data?.attributes || [];
        const transformedAttributes = rawAttributes
            .map(transformCartItemAttribute)
            .filter((attr): attr is TransformedCartItemAttribute => attr !== null);

        const transformedVariations = (rawItem.variation || []).map(
            (v): TransformedCartItemVariation => {
                // Find the matching transformed attribute to get the potentially translated attribute name
                const matchingAttribute = transformedAttributes.find(
                    (attr) => attr.taxonomy === v.attribute
                );
                const attributeDisplayName = matchingAttribute?.name ?? v.attribute; // Fallback to raw attribute name
                // Find the matching term within that attribute to get the potentially translated term name
                const termDisplayName = matchingAttribute?.term?.name ?? v.value; // Fallback to raw value
                return {
                    attribute: attributeDisplayName,
                    value: termDisplayName,
                };
            }
        );

        const transformCartItemTotals = (
            rawTotals: RawCartItemTotals
        ): TransformedCartItemTotals => {
            return {
                lineSubtotal: parseFloat(rawTotals.line_subtotal) / divisor,
                lineSubtotalTax: parseFloat(rawTotals.line_subtotal_tax) / divisor,
                lineTotal: parseFloat(rawTotals.line_total) / divisor,
                lineTotalTax: parseFloat(rawTotals.line_total_tax) / divisor,
                formattedLineSubtotal: formatMinorUnitString(
                    rawTotals.line_subtotal,
                    currencyMinorUnit
                ),
                formattedLineTotal: formatMinorUnitString(
                    rawTotals.line_total,
                    currencyMinorUnit
                ),
            };
        };

        return {
            key: rawItem.key,
            id: rawItem.id,
            quantity: rawItem.quantity,
            quantityLimits: rawItem.quantity_limits,
            name: finalName,
            shortDescription: finalShortDescription,
            description: finalDescription,
            permalink: rawItem.permalink,
            images: rawItem.images,
            variation: transformedVariations, // Use transformed variations
            price: price,
            regularPrice: regularPrice,
            salePrice: salePrice,
            formattedPrice: formatMinorUnitString(
                rawItem.prices.price,
                currencyMinorUnit
            ),
            formattedRegularPrice: formatMinorUnitString(
                rawItem.prices.regular_price,
                currencyMinorUnit
            ),
            formattedSalePrice: formatMinorUnitString(
                rawItem.prices.sale_price,
                currencyMinorUnit
            ),
            totals: transformCartItemTotals(rawItem.totals),
            sku: rawItem.sku,
            attributes: transformedAttributes, // Add transformed attributes
        };
    };

    const transformCartTotals = (
        rawTotals: RawCartTotals
    ): TransformedCartTotals => {
        const divisor = Math.pow(10, currencyMinorUnit);
        return {
            totalItems: parseFloat(rawTotals.total_items) / divisor,
            totalItemsTax: parseFloat(rawTotals.total_items_tax) / divisor,
            totalFees: parseFloat(rawTotals.total_fees) / divisor,
            totalFeesTax: parseFloat(rawTotals.total_fees_tax) / divisor,
            totalDiscount: parseFloat(rawTotals.total_discount) / divisor,
            totalDiscountTax: parseFloat(rawTotals.total_discount_tax) / divisor,
            totalShipping: parseFloat(rawTotals.total_shipping) / divisor,
            totalShippingTax: parseFloat(rawTotals.total_shipping_tax) / divisor,
            totalPrice: parseFloat(rawTotals.total_price) / divisor,
            totalTax: parseFloat(rawTotals.total_tax) / divisor,
            taxLines: (rawTotals.tax_lines || []).map((line) => ({
                name: line.name,
                price: parseFloat(line.price) / divisor,
                formattedPrice: formatMinorUnitString(line.price, currencyMinorUnit),
            })),
            currencyCode: rawTotals.currency_code,
            currencySymbol: rawTotals.currency_symbol,
            currencyMinorUnit: rawTotals.currency_minor_unit,
            currencyDecimalSeparator: rawTotals.currency_decimal_separator,
            currencyThousandSeparator: rawTotals.currency_thousand_separator,
            currencyPrefix: rawTotals.currency_prefix,
            currencySuffix: rawTotals.currency_suffix,
            formattedTotalItems: formatMinorUnitString(
                rawTotals.total_items,
                currencyMinorUnit
            ),
            formattedTotalFees: formatMinorUnitString(
                rawTotals.total_fees,
                currencyMinorUnit
            ),
            formattedTotalDiscount: formatMinorUnitString(
                rawTotals.total_discount,
                currencyMinorUnit
            ),
            formattedTotalShipping: formatMinorUnitString(
                rawTotals.total_shipping,
                currencyMinorUnit
            ),
            formattedTotalPrice: formatMinorUnitString(
                rawTotals.total_price,
                currencyMinorUnit
            ),
            formattedTotalTax: formatMinorUnitString(
                rawTotals.total_tax,
                currencyMinorUnit
            ),
        };
    };

    const transformCartCoupons = (
        rawCoupons: RawCartCoupon[]
    ): TransformedCartCoupon[] => {
        return rawCoupons.map((c) => ({
            code: c.code,
            discountType: c.discount_type,
        }));
    };

    const transformCartFees = (rawFees: RawCartFee[]): TransformedCartFee[] => {
        const divisor = Math.pow(10, currencyMinorUnit);
        return rawFees.map((f) => ({
            id: f.id,
            name: f.name,
            total: parseFloat(f.totals.total_price) / divisor, // Assuming fee amount is in total_price
            formattedTotal: formatMinorUnitString(
                f.totals.total_price,
                currencyMinorUnit
            ),
        }));
    };

    const transformSinglePackageShippingRates = (
        rawRates: RawCartShippingRate[]
    ): TransformedCartShippingRate[] => {
        const divisor = Math.pow(10, currencyMinorUnit);
        return rawRates.map((r) => ({
            rateId: r.rate_id,
            name: r.name,
            description: r.description,
            deliveryTime: r.delivery_time,
            price: parseFloat(r.price) / divisor,
            formattedPrice: formatMinorUnitString(r.price, currencyMinorUnit),
            taxes: parseFloat(r.taxes) / divisor,
            formattedTaxes: formatMinorUnitString(r.taxes, currencyMinorUnit),
            instanceId: r.instance_id,
            methodId: r.method_id,
            metaData: r.meta_data,
            selected: r.selected,
        }));
    };

    const transformShippingPackages = (
        rawPackages: RawCartShippingPackage[]
    ): TransformedCartShippingPackage[] => {
        return rawPackages.map((pkg) => ({
            packageId: pkg.package_id,
            name: pkg.name,
            destination: pkg.destination,
            items: pkg.items,
            shippingRates: transformSinglePackageShippingRates(pkg.shipping_rates),
        }));
    };

    const transformedItems = (rawCart.items || []).map(transformCartItem);
    const transformedTotals = transformCartTotals(rawCart.totals);
    const transformedCoupons = transformCartCoupons(rawCart.coupons || []);
    const transformedFees = transformCartFees(rawCart.fees || []);
    const transformedShippingPackages = transformShippingPackages(
        rawCart.shipping_rates || []
    );
    const transformedCrossSells = (rawCart.cross_sells || [])
        .map(transformProductData)
        .filter((p): p is TransformedProduct => p !== null);

    return {
        items: transformedItems,
        coupons: transformedCoupons,
        fees: transformedFees,
        shippingRates: transformedShippingPackages,
        shippingAddress: transformAddress(rawCart.shipping_address),
        billingAddress: transformAddress(rawCart.billing_address),
        totals: transformedTotals,
        itemsCount: rawCart.items_count || 0,
        itemsWeight: rawCart.items_weight || 0,
        needsPayment: rawCart.needs_payment || false,
        needsShipping: rawCart.needs_shipping || false,
        paymentRequirements: rawCart.payment_requirements || [],
        hasCalculatedShipping: rawCart.has_calculated_shipping || false,
        crossSells: transformedCrossSells,
        errors: rawCart.errors || [],
        paymentMethods: rawCart.payment_methods || [],
    };
};

// --- API Slice ---
export const cartApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the current cart.
         */
        getCart: builder.query < TransformedCart, void> ({
            query: () => ({ url: `${WC_STORE_API_BASE}/cart`, method: "GET" }),
            transformResponse: (response: RawCart): TransformedCart => {
                return transformCartData(response);
            },
            providesTags: ["Cart"],
        }),

        /**
         * Adds an item to the cart.
         */
        addItemToCart: builder.mutation < TransformedCart, AddCartItemParams> ({
            query: (item) => ({
                url: `${WC_STORE_API_BASE}/cart/add-item`,
                method: "POST",
                body: item,
            }),
            transformResponse: (response: RawCart): TransformedCart => {
                return transformCartData(response);
            },
            invalidatesTags: ["Cart"],
        }),

        /**
         * Updates an item in the cart.
         */
        updateCartItem: builder.mutation < TransformedCart, UpdateCartItemParams> ({
            query: (item) => ({
                url: `${WC_STORE_API_BASE}/cart/update-item`,
                method: "POST",
                body: item,
            }),
            transformResponse: (response: RawCart): TransformedCart => {
                return transformCartData(response);
            },
            invalidatesTags: ["Cart"],
        }),

    /**
     * Removes an item from the cart.
     */
    removeItemFromCart: builder.mutation < TransformedCart, RemoveCartItemParams > (
        {
            query: (item) => ({
                url: `${WC_STORE_API_BASE}/cart/remove-item`,
                method: "POST",
                body: item,
            }),
            transformResponse: (response: RawCart): TransformedCart => {
                return transformCartData(response);
            },
            invalidatesTags: ["Cart"],
        }
    ),

    /**
     * Applies a coupon to the cart.
     */
    applyCoupon: builder.mutation < TransformedCart, ApplyCouponParams > ({
        query: (coupon) => ({
            url: `${WC_STORE_API_BASE}/cart/apply-coupon`,
            method: "POST",
            body: coupon,
        }),
        transformResponse: (response: RawCart): TransformedCart => {
            return transformCartData(response);
        },
        invalidatesTags: ["Cart"],
    }),

    /**
     * Removes a coupon from the cart.
     */
    removeCoupon: builder.mutation < TransformedCart, RemoveCouponParams > ({
        query: (coupon) => ({
            url: `${WC_STORE_API_BASE}/cart/remove-coupon`,
            method: "POST",
            body: coupon,
        }),
        transformResponse: (response: RawCart): TransformedCart => {
            return transformCartData(response);
        },
        invalidatesTags: ["Cart"],
    }),

    /**
     * Updates customer billing and/or shipping address in the cart.
     * Accepts TransformedAddress, transforms to snake_case for API.
     */
    updateCustomer: builder.mutation < TransformedCart, UpdateCustomerParams > ({
        query: (customerData) => {
            const billingAddressForApi = transformAddressToRaw(
                customerData.billingAddress
            );
            const shippingAddressForApi = transformAddressToRaw(
                customerData.shippingAddress
            );
            const bodyPayload: {
                billing_address?: Partial<RawCartBillingAddress>;
                shipping_address?: Partial<RawCartShippingAddress>;
            } = {};
            if (billingAddressForApi)
                bodyPayload.billing_address = billingAddressForApi;
            if (shippingAddressForApi)
                bodyPayload.shipping_address = shippingAddressForApi;

            return {
                url: `${WC_STORE_API_BASE}/cart/update-customer`,
                method: "POST",
                body: bodyPayload,
            };
        },
        transformResponse: (response: RawCart): TransformedCart => {
            return transformCartData(response);
        },
        invalidatesTags: ["Cart"],
    }),

    /**
     * Selects a shipping rate for a specific package.
     */
    selectShippingRate: builder.mutation <
TransformedCart,
    SelectShippingRateParams
    > ({
        query: (shippingRateData) => ({
            url: `${WC_STORE_API_BASE}/cart/select-shipping-rate`,
            method: "POST",
            body: shippingRateData,
        }),
        transformResponse: (response: RawCart): TransformedCart => {
            return transformCartData(response);
        },
        invalidatesTags: ["Cart"],
    }),

    /**
     * Clears all items from the cart using the correct endpoint.
     */
    clearCart: builder.mutation < TransformedCart, void> ({
        query: () => ({
            url: `${WC_STORE_API_BASE}/cart/items`, // Correct endpoint
            method: "DELETE",
        }),
        transformResponse: (response: RawCart): TransformedCart => {
            console.log(
                "[Cart API] Cart cleared via DELETE /cart/items response:",
                response
            );
            return transformCartData(response);
        },
        invalidatesTags: ["Cart"],
    }),
  }),
overrideExisting: false,
});

// --- Exports ---
export const {
    useGetCartQuery,
    useAddItemToCartMutation,
    useUpdateCartItemMutation,
    useRemoveItemFromCartMutation,
    useApplyCouponMutation,
    useRemoveCouponMutation,
    useUpdateCustomerMutation,
    useSelectShippingRateMutation,
    useClearCartMutation,
} = cartApi;

export const {
    getCart,
    addItemToCart,
    updateCartItem,
    removeItemFromCart,
    applyCoupon,
    removeCoupon,
    updateCustomer,
    selectShippingRate,
    clearCart,
} = cartApi.endpoints;